import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import {
  AlertTriangle,
  Clock,
  Flag,
  CheckCircle2,
  Users,
  Trophy,
  UserX,
  TrendingUp,
  Calendar,
  Activity,
  RefreshCw,
  Eye,
} from "lucide-react";
import RepairOverflowButton from "../components/RepairOverflowButton";
import BackfillChatForfeitsButton from "../components/BackfillChatForfeitsButton";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatTile({ icon: Icon, label, value, testId, tone = "default", sub }) {
  const tones = {
    default: "border-[#E4E4E7] dark:border-[#2A2A30]",
    warn: "border-[#EA580C]/60 border-l-2 border-l-[#EA580C]",
    danger: "border-[#E11D48]/60 border-l-2 border-l-[#E11D48]",
    success: "border-[#34C759]/60 border-l-2 border-l-[#34C759]",
    info: "border-[#7C3AED]/60 border-l-2 border-l-[#7C3AED]",
  };
  const iconColors = {
    default: "text-[#71717A]",
    warn: "text-[#EA580C]",
    danger: "text-[#E11D48]",
    success: "text-[#34C759]",
    info: "text-[#7C3AED]",
  };
  return (
    <div
      data-testid={testId}
      className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border ${tones[tone]} p-5 flex flex-col gap-1`}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
          {label}
        </span>
        {Icon && <Icon className={`w-4 h-4 ${iconColors[tone]}`} />}
      </div>
      <div className="font-heading text-3xl text-[#09090B] dark:text-[#F4F4F5]">{value}</div>
      {sub && <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">{sub}</div>}
    </div>
  );
}

function WeekProgressBar({ week }) {
  const pct = week.completion_pct;
  const scheduled = week.scheduled || 0;
  return (
    <div data-testid="week-progress" className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">
            // this week
          </span>
          <span className="font-mono text-[10px] text-[#71717A]">
            {week.window_start} → {week.window_end}
          </span>
        </div>
        <span className="font-heading text-2xl text-[#09090B] dark:text-[#F4F4F5]" data-testid="week-completion">
          {pct}%
        </span>
      </div>
      <div className="h-3 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#7C3AED] to-[#EA580C]"
          style={{ width: `${pct}%` }}
          data-testid="week-progress-bar"
        />
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 font-mono text-xs">
        <div><span className="text-[#71717A]">SCHEDULED</span><div className="text-[#09090B] dark:text-[#F4F4F5] text-lg font-heading" data-testid="week-scheduled">{scheduled}</div></div>
        <div><span className="text-[#34C759]">MATCHED</span><div className="text-[#34C759] text-lg font-heading" data-testid="week-confirmed">{week.confirmed}</div></div>
        <div><span className="text-[#71717A]">PENDING</span><div className="text-[#09090B] dark:text-[#F4F4F5] text-lg font-heading" data-testid="week-pending">{week.pending}</div></div>
        <div><span className="text-[#EA580C]">OVERDUE</span><div className="text-[#EA580C] text-lg font-heading" data-testid="week-overdue">{week.overdue}</div></div>
        <div><span className="text-[#E11D48]">DISPUTED</span><div className="text-[#E11D48] text-lg font-heading" data-testid="week-disputed">{week.disputed}</div></div>
      </div>
    </div>
  );
}

function UsagePanel() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics/usage?days=14");
      setUsage(data);
    } catch (e) {
      /* silently fail — the outer analytics toast covers this */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  if (loading) return <TactLoader label="LOADING USAGE" />;
  if (!usage) return null;

  const series = usage.series || [];
  const maxImp = Math.max(1, ...series.map((s) => s.impressions));
  const maxDau = Math.max(1, ...series.map((s) => s.dau));

  return (
    <div
      className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5"
      data-testid="usage-panel"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#00FF87]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF87]">
            // audience · last 14 days
          </span>
        </div>
        <span className="font-mono text-[10px] text-[#71717A]">
          dedupe: (user · route · 5m) · ttl 90d
        </span>
      </div>

      {/* Headline tiles — today + 7d rolling totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#00FF87] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">DAU TODAY</div>
          <div className="font-heading text-3xl text-[#00FF87]" data-testid="usage-dau-today">
            {usage.today_dau}
          </div>
          <div className="font-mono text-[10px] text-[#71717A]">unique logged-in users</div>
        </div>
        <div className="border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#7C3AED] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">IMPRESSIONS TODAY</div>
          <div className="font-heading text-3xl text-[#7C3AED]" data-testid="usage-impressions-today">
            {usage.today_impressions}
          </div>
          <div className="font-mono text-[10px] text-[#71717A]">
            {usage.today_impressions_authed} authed · {usage.today_impressions_anon} anon
          </div>
        </div>
        <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">7D IMPRESSIONS</div>
          <div className="font-heading text-3xl text-[#09090B] dark:text-[#F4F4F5]" data-testid="usage-impressions-7d">
            {usage.totals_7d?.impressions || 0}
          </div>
          <div className="font-mono text-[10px] text-[#71717A]">
            +{usage.totals_7d?.impressions_anon || 0} anon
          </div>
        </div>
        <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">TOP ROUTES TODAY</div>
          <ul className="font-mono text-[11px] text-[#09090B] dark:text-[#F4F4F5] space-y-0.5 mt-1" data-testid="usage-top-routes">
            {(usage.top_routes_today || []).length === 0 ? (
              <li className="text-[#71717A]">—</li>
            ) : (
              usage.top_routes_today.map((r) => (
                <li key={r.route} className="flex items-center justify-between gap-2">
                  <span className="truncate">{r.route}</span>
                  <span className="text-[#7C3AED]">{r.count}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {/* 14-day sparkline table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="usage-table">
          <thead>
            <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
              <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">DAY</th>
              <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">DAU</th>
              <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">IMPR.</th>
              <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">ANON</th>
              <th className="py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">TREND</th>
            </tr>
          </thead>
          <tbody>
            {series.map((row) => {
              const impPct = Math.round((row.impressions / maxImp) * 100);
              const dauPct = Math.round((row.dau / maxDau) * 100);
              return (
                <tr
                  key={row.day}
                  className={`border-b border-[#E4E4E7] dark:border-[#2A2A30] ${row.day === usage.today ? "bg-[#00FF87]/5" : ""}`}
                  data-testid={`usage-row-${row.day}`}
                >
                  <td className="py-2 px-2 font-mono text-xs text-[#09090B] dark:text-[#F4F4F5]">
                    {row.day === usage.today && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00FF87] mr-2 animate-pulse" aria-hidden />
                    )}
                    {row.day}
                  </td>
                  <td className="py-2 px-2 text-right font-mono text-sm text-[#00FF87]">{row.dau}</td>
                  <td className="py-2 px-2 text-right font-mono text-sm text-[#7C3AED]">{row.impressions}</td>
                  <td className="py-2 px-2 text-right font-mono text-xs text-[#71717A]">{row.impressions_anon}</td>
                  <td className="py-2 px-2 w-[35%]">
                    <div className="flex flex-col gap-0.5">
                      <div className="h-1.5 bg-[#F4F4F5] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30]">
                        <div className="h-full bg-[#7C3AED]" style={{ width: `${impPct}%` }} />
                      </div>
                      <div className="h-1.5 bg-[#F4F4F5] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30]">
                        <div className="h-full bg-[#00FF87]" style={{ width: `${dauPct}%` }} />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A] flex gap-3">
        <span><span className="inline-block w-2 h-2 bg-[#7C3AED] mr-1 align-middle" />impressions</span>
        <span><span className="inline-block w-2 h-2 bg-[#00FF87] mr-1 align-middle" />DAU</span>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch (e) {
      toast.error(`Analytics failed to load: ${formatApiError(e)}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(() => refresh(false), 60000);
    return () => clearInterval(iv);
  }, []);

  if (loading || !data) return <TactLoader label="LOADING ANALYTICS" />;

  const c = data.counters;
  const a24 = data.activity_24h;
  const a7 = data.activity_7d;

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-1">
            // admin overview
          </div>
          <h2 className="font-heading text-3xl uppercase tracking-wider">Analytics</h2>
          <div className="font-mono text-[10px] text-[#71717A] mt-1">
            Generated {fmtDate(data.generated_at)} · auto-refreshes every 30s
          </div>
        </div>
        <button
          data-testid="analytics-refresh-btn"
          onClick={() => refresh(true)}
          disabled={refreshing}
          className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          REFRESH
        </button>
      </div>

      <RepairOverflowButton />
      <BackfillChatForfeitsButton />

      {/* Headline counters (the 3 things the user asked for + overdue) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile
          testId="stat-pending"
          icon={Clock}
          label="PENDING GAMES"
          value={c.pending_matches}
          tone="info"
          sub={`${c.pending_awaiting_both} awaiting both · ${c.pending_awaiting_one} awaiting 1`}
        />
        <StatTile
          testId="stat-disputes"
          icon={AlertTriangle}
          label="DISPUTES"
          value={c.disputed_matches}
          tone="danger"
          sub="score mismatch · needs override"
        />
        <StatTile
          testId="stat-reports"
          icon={Flag}
          label="REPORTS"
          value={c.total_reports}
          tone="warn"
          sub={`+${c.reports_last_7d} in last 7d`}
        />
        <StatTile
          testId="stat-overdue"
          icon={AlertTriangle}
          label="OVERDUE"
          value={c.overdue_matches}
          tone="warn"
          sub="past scheduled time"
        />
      </div>

      {/* Week progress */}
      <WeekProgressBar week={data.week} />

      {/* Secondary counters — players + leagues */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile
          testId="stat-users"
          icon={Users}
          label="TOTAL PLAYERS"
          value={c.total_users}
          sub={`+${c.new_users_24h} in 24h · +${c.new_users_7d} in 7d`}
        />
        <StatTile
          testId="stat-banned"
          icon={UserX}
          label="BANNED PLAYERS"
          value={c.banned_users}
          tone={c.banned_users > 0 ? "danger" : "default"}
        />
        <StatTile
          testId="stat-leagues-open"
          icon={Trophy}
          label="OPEN LEAGUES"
          value={c.open_leagues}
          tone="info"
          sub={`${c.locked_leagues} locked`}
        />
        <StatTile
          testId="stat-confirmed"
          icon={CheckCircle2}
          label="CONFIRMED (ALL TIME)"
          value={c.confirmed_matches}
          tone="success"
        />
      </div>

      {/* Activity rail */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid="activity-rail">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">
            // activity pulse
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Confirmed 24h</div>
            <div className="font-heading text-2xl text-[#34C759]" data-testid="activity-confirmed-24h">{a24.confirmed_matches}</div>
            <div className="font-mono text-[10px] text-[#71717A]">{a7.confirmed_matches} in 7d</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Auto-disputed 24h</div>
            <div className="font-heading text-2xl text-[#E11D48]" data-testid="activity-disputed-24h">{a24.auto_disputed}</div>
            <div className="font-mono text-[10px] text-[#71717A]">EOD sweep trigger</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">New reports 24h</div>
            <div className="font-heading text-2xl text-[#EA580C]" data-testid="activity-reports-24h">{a24.new_reports}</div>
            <div className="font-mono text-[10px] text-[#71717A]">{a7.new_reports} in 7d</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">New players 24h</div>
            <div className="font-heading text-2xl text-[#7C3AED]" data-testid="activity-users-24h">{a24.new_users}</div>
            <div className="font-mono text-[10px] text-[#71717A]">{a7.new_users} in 7d</div>
          </div>
        </div>
      </div>

      {/* Audience — DAU + impressions (14-day rolling) */}
      <UsagePanel />

      {/* Top reported players */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid="top-reported-panel">
        <div className="flex items-center gap-2 mb-4">
          <Flag className="w-4 h-4 text-[#EA580C]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">
            // most-reported players
          </span>
        </div>
        {data.top_reported.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO REPORTS ON RECORD. ALL QUIET ON THE ePLAY FRONT. ]</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
                  <th className="text-left py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">PLAYER</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">REPORTS</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">LEAGUES</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">LAST</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {data.top_reported.map((r) => {
                  const ratio = r.unique_reporter_count / r.threshold;
                  const pctCls = ratio >= 1 ? "text-[#E11D48]" : ratio >= 0.6 ? "text-[#EA580C]" : "text-[#71717A]";
                  return (
                    <tr key={r.user_id} className="border-b border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`reported-row-${r.user_id}`}>
                      <td className="py-2 font-heading uppercase">{r.username}</td>
                      <td className={`py-2 text-right font-mono ${pctCls}`}>{r.unique_reporter_count} / {r.threshold}</td>
                      <td className="py-2 text-right font-mono text-[#71717A]">{r.league_count}</td>
                      <td className="py-2 text-right font-mono text-[10px] text-[#71717A]">{fmtDate(r.last_reported_at)}</td>
                      <td className="py-2 text-right">
                        {r.banned ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48] border border-[#E11D48] px-2 py-0.5">BANNED</span>
                        ) : ratio >= 1 ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48] border border-[#E11D48] px-2 py-0.5">AT LIMIT</span>
                        ) : ratio >= 0.6 ? (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#EA580C] border border-[#EA580C] px-2 py-0.5">WARN</span>
                        ) : (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent disputes — action list */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid="recent-disputes-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#E11D48]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">
              // disputes awaiting override
            </span>
          </div>
          <span className="font-mono text-[10px] text-[#71717A]">top 5</span>
        </div>
        {data.recent_disputes.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO ACTIVE DISPUTES. ]</div>
        ) : (
          <div className="space-y-2">
            {data.recent_disputes.map((d) => (
              <Link
                key={d.match_id}
                to={`/matches/${d.match_id}`}
                className="flex items-center justify-between gap-3 border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#E11D48] px-4 py-3 hover:border-l-[#7C3AED] hover:border-[#7C3AED] transition-colors"
                data-testid={`dispute-row-${d.match_id}`}
              >
                <div>
                  <div className="font-heading uppercase">
                    {d.home_username} <span className="text-[#71717A] font-mono text-xs">VS</span> {d.away_username}
                  </div>
                  <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">
                    {d.league_name || "League"} · RD {d.round} · {fmtDate(d.scheduled_at)}
                  </div>
                </div>
                <div className="font-mono text-xs text-[#71717A]">
                  {d.home_score ?? "—"} : {d.away_score ?? "—"}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Overdue matches */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid="recent-overdue-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#EA580C]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">
              // overdue matches
            </span>
          </div>
          <span className="font-mono text-[10px] text-[#71717A]">top 5 · oldest first</span>
        </div>
        {data.recent_overdue.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO OVERDUE MATCHES. SCHEDULE IS ON TRACK. ]</div>
        ) : (
          <div className="space-y-2">
            {data.recent_overdue.map((m) => (
              <Link
                key={m.match_id}
                to={`/matches/${m.match_id}`}
                className="flex items-center justify-between gap-3 border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#EA580C] px-4 py-3 hover:border-l-[#7C3AED] hover:border-[#7C3AED] transition-colors"
                data-testid={`overdue-row-${m.match_id}`}
              >
                <div>
                  <div className="font-heading uppercase">
                    {m.home_username} <span className="text-[#71717A] font-mono text-xs">VS</span> {m.away_username}
                  </div>
                  <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">
                    {m.league_name || "League"} · RD {m.round} · due {fmtDate(m.scheduled_at)}
                  </div>
                </div>
                <div className="flex gap-1 font-mono text-[9px] uppercase tracking-[0.2em]">
                  <span className={m.home_submitted ? "text-[#34C759]" : "text-[#71717A]"}>
                    H:{m.home_submitted ? "✓" : "·"}
                  </span>
                  <span className={m.away_submitted ? "text-[#34C759]" : "text-[#71717A]"}>
                    A:{m.away_submitted ? "✓" : "·"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Leagues snapshot */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5" data-testid="leagues-panel">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">
            // leagues roster
          </span>
        </div>
        {data.leagues.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO LEAGUES YET. ]</div>
        ) : (
          <div className="space-y-2">
            {data.leagues.map((lg) => (
              <Link
                key={lg.id}
                to={`/leagues/${lg.id}`}
                className="flex items-center justify-between gap-4 border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 hover:border-[#7C3AED] transition-colors"
                data-testid={`league-row-${lg.id}`}
              >
                <div className="font-heading uppercase">
                  {lg.name}{" "}
                  <span className={`font-mono text-[10px] tracking-[0.2em] ml-2 ${lg.status === "open" ? "text-[#7C3AED]" : "text-[#71717A]"}`}>
                    {lg.status.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-3 min-w-[200px]">
                  <div className="flex-1 h-1.5 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30]">
                    <div className="h-full bg-[#7C3AED]" style={{ width: `${lg.fill_pct}%` }} />
                  </div>
                  <span className="font-mono text-xs text-[#71717A] whitespace-nowrap">
                    {lg.player_count}/{lg.capacity}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
